import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Eye,
  Bot,
  ArrowLeft,
  Lock,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import LemonBotPanel from "@/components/LemonBotPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem("token");
  return { headers: { Authorization: `Bearer ${token}` } };
}

const SURFACES = [
  {
    id: "dashboard",
    title: "Student Dashboard",
    activity: "preview_dashboard",
    panelTitle: "LemonBot · Dashboard",
    subtitle: "How LemonBot looks on the dashboard surface",
    quickActions: undefined, // use defaults
  },
  {
    id: "workbook",
    title: "Workbook (Reflections)",
    activity: "preview_workbook",
    panelTitle: "LemonBot · Workbook coach",
    subtitle: "How LemonBot looks while a student is doing reflections",
    quickActions: [
      {
        intent: "explain_easier",
        label: "Stuck on a reflection",
        prompt:
          "I'm stuck on one of my workbook reflections. Ask me 2 short questions that will help me find my answer.",
      },
      {
        intent: "give_example",
        label: "Show example",
        prompt:
          "Show me ONE strong session reflection. Keep it under 50 words.",
      },
    ],
  },
  {
    id: "builder",
    title: "Business Plan Builder",
    activity: "preview_business_plan_builder",
    panelTitle: "LemonBot · Section coach",
    subtitle: "How LemonBot coaches a Mini Business Plan section",
    quickActions: [
      {
        intent: "business_plan_help",
        label: "Coach my draft",
        prompt:
          'Look at my current draft for the "Business Idea" section and ask me 2 short questions to sharpen it.',
      },
      {
        intent: "give_example",
        label: "Show example",
        prompt:
          'Give me ONE strong example of a "Business Idea" section. Keep it under 60 words.',
      },
    ],
  },
  {
    id: "pitch",
    title: "Pitch Practice",
    activity: "preview_pitch_practice",
    panelTitle: "LemonBot · Pitch coach",
    subtitle: "How LemonBot coaches a 30-second pitch",
    quickActions: [
      {
        intent: "pitch_practice",
        label: "Give me a template",
        prompt:
          "Give me a 30-second pitch template I can fill in (Hook / Problem-Solution / Ask). Under 80 words.",
      },
      {
        intent: "explain_easier",
        label: "I'm nervous",
        prompt:
          "I'm nervous about pitching. Give me ONE 5-minute calming exercise plus ONE thing to practice.",
      },
    ],
  },
];

function StudentPreviewPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [active, setActive] = useState("dashboard");
  const [usageBefore, setUsageBefore] = useState(null);
  const [classes, setClasses] = useState([]);

  // Snapshot total LemonBot messages BEFORE preview so we can prove later
  // that nothing was logged during the educator's preview session.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/classes`, authHeaders());
        if (cancelled) return;
        const cls = res.data || [];
        setClasses(cls);
        if (cls.length) {
          const u = await axios.get(
            `${API}/educator/lemonbot/usage?class_id=${cls[0].id}`,
            authHeaders()
          );
          if (!cancelled) setUsageBefore(u.data?.total_messages || 0);
        }
      } catch {
        // best-effort
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const surface = SURFACES.find((s) => s.id === active) || SURFACES[0];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-fuchsia-50 to-orange-50">
      <Navbar user={user} onLogout={onLogout} />
      <div className="max-w-5xl mx-auto px-4 py-6">
        <button
          onClick={() => navigate(-1)}
          data-testid="preview-back-btn"
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-3 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="bg-gradient-to-br from-fuchsia-600 via-purple-600 to-indigo-600 rounded-2xl p-5 text-white shadow-lg mb-5">
          <div className="flex items-center gap-2 mb-1">
            <Eye className="w-5 h-5" />
            <h1 className="text-xl font-bold">Student Preview Mode</h1>
            <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide bg-white/15 border border-white/30 rounded-full px-2 py-0.5">
              <Bot className="w-3 h-3" />
              LemonBot
            </span>
          </div>
          <p className="text-white/85 text-sm max-w-xl">
            Try LemonBot exactly like a student would — same prompt, same
            quick actions, same scoring — but{" "}
            <strong>nothing you do here is saved</strong>. Pitch attempts
            are not stored, no usage analytics are recorded, and no readiness
            scores change.
          </p>
        </div>

        {/* Privacy notice */}
        <div
          className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-5"
          data-testid="preview-privacy-notice"
        >
          <Lock className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-emerald-800">
            All requests run with{" "}
            <code className="bg-emerald-100 px-1 py-0.5 rounded">
              preview_mode=true
            </code>{" "}
            — backend skips the <code>lemonbot_messages</code> insert and
            educator-side usage counters never increment.
          </div>
        </div>

        {/* Surface selector */}
        <div
          className="flex flex-wrap gap-1.5 mb-4"
          role="tablist"
          aria-label="Preview surfaces"
        >
          {SURFACES.map((s) => {
            const selected = active === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setActive(s.id)}
                data-testid={`preview-tab-${s.id}`}
                role="tab"
                aria-selected={selected}
                className={`text-sm font-medium px-3 py-1.5 rounded-full border transition-colors ${
                  selected
                    ? "bg-purple-600 text-white border-purple-600"
                    : "bg-white text-gray-700 border-gray-300 hover:border-purple-400"
                }`}
              >
                {s.title}
              </button>
            );
          })}
        </div>

        {/* Active surface */}
        <div
          className="bg-white rounded-2xl border border-gray-200 p-5"
          data-testid={`preview-surface-${surface.id}`}
        >
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
            Surface preview
          </div>
          <h2 className="text-lg font-bold text-gray-900 mb-1">
            {surface.title}
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            This is how LemonBot will appear to a student on this surface.
            Try the chips and free-form input — none of it persists.
          </p>

          <LemonBotPanel
            previewMode
            openByDefault
            activity={surface.activity}
            title={surface.panelTitle}
            subtitle={surface.subtitle}
            quickActions={surface.quickActions}
          />
        </div>

        {usageBefore !== null && (
          <div className="mt-4 text-xs text-gray-500" data-testid="preview-baseline">
            <CheckCircle2 className="w-3.5 h-3.5 inline mr-1 text-emerald-500" />
            Class baseline before you started: <strong>{usageBefore}</strong>{" "}
            messages logged. Refresh your educator dashboard after this
            session to confirm the count hasn't changed.
          </div>
        )}

        {classes.length === 0 && (
          <div className="mt-4 text-xs text-gray-500 inline-flex items-center gap-1">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Loading your classes…
          </div>
        )}
      </div>
    </div>
  );
}

export default StudentPreviewPage;
